import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, AlertTitle, Button, Link, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { useToast } from "../../common/hooks/useToast";
import { Popover } from "../../common/popover/Popover";
import { describeError, getRateLimitRejection } from "../../common/rateLimit";
import { Item } from "../../styles/common";
import { SET_RECAPTCHA_SCORE_THRESHOLD } from "./appConfig.mutation";
import { RECAPTCHA_SETTINGS } from "./appConfig.queries";
import { fieldErrorOf } from "./nearbyAlerts";
import {
  describeSource,
  describeThreshold,
  PROPAGATION_NOTICE,
  RecaptchaSettings,
  validateThreshold,
} from "./recaptcha";

/**
 * The score a request must reach before it gets a sign-in code.
 *
 * Its own component with its own read, like the forced-update panel, so a failure
 * here renders in this card rather than in place of the screen. Nothing polls.
 * A save is confirmed first, because it changes who can sign in straight away.
 */
export const RecaptchaPanel: React.FC = (): JSX.Element => {
  const { data, loading, error } = useQuery<{ recaptchaSettings: RecaptchaSettings }>(RECAPTCHA_SETTINGS, {
    fetchPolicy: "network-only",
  });

  /** The result has no id, so it is written over the query's copy rather than merged. */
  const [save] = useMutation(SET_RECAPTCHA_SCORE_THRESHOLD, {
    update(cache, { data: result }) {
      if (!result?.setRecaptchaScoreThreshold) return;

      cache.writeQuery({
        query: RECAPTCHA_SETTINGS,
        data: { recaptchaSettings: result.setRecaptchaScoreThreshold },
      });
    },
  });

  const toast = useToast();

  /** `null` until the admin types: the input mirrors the stored score until then. */
  const [draft, setDraft] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const settings = data?.recaptchaSettings;
  const value = draft ?? (settings ? String(settings.scoreThreshold) : "");
  const score = Number(value);
  const valid = Boolean(settings) && !validateThreshold(value, settings?.min, settings?.max);
  const dirty = valid && score !== settings?.scoreThreshold;

  const handleSaveClick = () => {
    const message = validateThreshold(value, settings?.min, settings?.max);

    if (message) {
      setFieldError(message);
      return;
    }

    setConfirming(true);
  };

  const write = async () => {
    setSaving(true);

    try {
      await save({ variables: { threshold: score } });
      toast.success(`reCAPTCHA score set to ${score}.`);
      setDraft(null);
      setFieldError("");
    } catch (writeError) {
      const named = fieldErrorOf(writeError);

      if (named) {
        setFieldError(named.message);
      } else if (!getRateLimitRejection(writeError)) {
        // The global Apollo link already announces a rate-limit rejection.
        toast.error(describeError(writeError, "Could not save the score."));
      }
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <Item sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        reCAPTCHA score
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Google scores each request for a sign-in code from 0 (likely a bot) to 1 (likely a person). A request below this
        score is refused, by text and by email, and on sign-up without a code. Too many refusals of real people show on{" "}
        <Link component={RouterLink} to="/reports?tab=otp">
          the OTP failures report
        </Link>
        .
      </Typography>

      {error && <ErrorNotifier error={error} />}

      {settings?.warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {settings.warning}
        </Alert>
      )}

      {settings && (
        <Alert severity={describeThreshold(settings.scoreThreshold).severity} sx={{ mb: 2 }}>
          <AlertTitle>In force now: {settings.scoreThreshold}</AlertTitle>
          <Typography variant="body2">
            {describeThreshold(settings.scoreThreshold).text} {describeSource(settings)}
          </Typography>
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Minimum score"
          value={value}
          disabled={saving || loading || !settings}
          error={Boolean(fieldError)}
          helperText={fieldError || "A number from 0 to 1, for example 0.25."}
          inputProps={{ inputMode: "decimal", "aria-label": "Minimum score" }}
          sx={{ minWidth: 260 }}
          onChange={(event) => {
            setDraft(event.target.value);
            setFieldError("");
          }}
        />

        <Button variant="contained" disabled={!dirty || saving} onClick={handleSaveClick} sx={{ mt: 0.25 }}>
          {saving ? "Saving…" : "Save score"}
        </Button>
      </Stack>

      {dirty && (
        <Alert severity={describeThreshold(score).severity} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{score}:</strong> {describeThreshold(score).text}
          </Typography>
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" component="p">
        {PROPAGATION_NOTICE}
      </Typography>

      <Popover open={confirming} handleClose={() => setConfirming(false)}>
        <Item sx={{ p: 3, maxWidth: 520, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Change the score from {settings?.scoreThreshold} to {score}?
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {describeThreshold(score).text}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirming(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" disabled={saving} onClick={write}>
              {saving ? "Saving…" : `Set to ${score}`}
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Item>
  );
};
